import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { EmptyState } from "@/components/Loaders";
import { Rocket, ArrowUp, MessageSquare, Plus, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Community() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [sort, setSort] = useState("new");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", tech_stack: "", url: "", problem: "", pricing: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => api.get(`/posts?sort=${sort}`).then((r) => setPosts(r.data)), [sort]);
  useEffect(() => { load(); }, [load]);

  const upvote = async (id) => {
    if (!user) { login(); return; }
    await api.post(`/posts/${id}/upvote`);
    load();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast("Add a title"); return; }
    setSubmitting(true);
    try { await api.post("/posts", form); toast.success("Project launched!"); setOpen(false); setForm({ title: "", description: "", tech_stack: "", url: "", problem: "", pricing: "" }); load(); }
    catch { toast.error("Failed"); } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-50 flex items-center gap-3"><Rocket className="w-8 h-8 text-cyan-400" />Show Us What You Built</h1>
          <p className="text-slate-400 mt-1">Launch your project, get feedback, and inspire other builders.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { if (o && !user) { login(); return; } setOpen(o); }}>
          <DialogTrigger asChild><Button className="bg-cyan-500 text-slate-950 font-semibold" data-testid="new-post-btn"><Plus className="w-4 h-4 mr-1.5" />Submit Project</Button></DialogTrigger>
          <DialogContent className="glass border-white/10 text-slate-200 max-w-lg">
            <DialogHeader><DialogTitle className="text-slate-50">Launch your project</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label className="text-slate-400 text-xs">Project name</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white/5 border-white/10 text-slate-200" data-testid="post-title-input" /></div>
              <div><Label className="text-slate-400 text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white/5 border-white/10 text-slate-200" data-testid="post-desc-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-400 text-xs">Tech stack</Label><Input value={form.tech_stack} onChange={(e) => setForm({ ...form, tech_stack: e.target.value })} className="bg-white/5 border-white/10 text-slate-200" /></div>
                <div><Label className="text-slate-400 text-xs">Pricing</Label><Input value={form.pricing} onChange={(e) => setForm({ ...form, pricing: e.target.value })} className="bg-white/5 border-white/10 text-slate-200" /></div>
              </div>
              <div><Label className="text-slate-400 text-xs">Website URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://" className="bg-white/5 border-white/10 text-slate-200" /></div>
              <Button type="submit" disabled={submitting} className="w-full bg-cyan-500 text-slate-950 font-semibold" data-testid="post-submit-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Launch Project"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2 mb-6">
        {["new", "top"].map((s) => <button key={s} onClick={() => setSort(s)} className={`px-3 py-1.5 rounded-lg text-sm border capitalize ${sort === s ? "border-cyan-500 bg-cyan-500/15 text-cyan-200" : "border-white/10 text-slate-400"}`}>{s === "new" ? "Newest" : "Top"}</button>)}
      </div>

      {posts.length === 0 ? (
        <EmptyState icon={Rocket} title="No projects yet" subtitle="Be the first to launch and show the community what you built." />
      ) : (
        <div className="space-y-4">
          {posts.map((p) => (
            <div key={p.id} className="glass rounded-xl p-5 flex gap-4 card-hover" data-testid={`post-${p.id}`}>
              <button onClick={() => upvote(p.id)} className="flex flex-col items-center gap-1 shrink-0 px-3 py-1.5 rounded-lg border border-white/10 hover:border-cyan-500/40 h-fit" data-testid={`upvote-${p.id}`}>
                <ArrowUp className={`w-4 h-4 ${p.upvoted_by?.includes(user?.user_id) ? "text-cyan-400" : "text-slate-500"}`} />
                <span className="font-mono text-sm text-slate-300">{p.upvotes}</span>
              </button>
              <div className="flex-1 cursor-pointer" onClick={() => navigate(`/community/${p.id}`)}>
                <h3 className="text-lg font-semibold text-slate-100 hover:text-cyan-300">{p.title}</h3>
                <p className="text-sm text-slate-400 mt-1 line-clamp-2">{p.description}</p>
                <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5"><Avatar className="w-5 h-5"><AvatarImage src={p.author_picture} /><AvatarFallback className="text-[8px] bg-cyan-500/20 text-cyan-300">{p.author_name?.[0]}</AvatarFallback></Avatar>{p.author_name}</span>
                  {p.tech_stack && <span className="font-mono">{p.tech_stack}</span>}
                  <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" />{p.comments_count}</span>
                  {p.url && <a href={p.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 text-cyan-400">Visit <ExternalLink className="w-3 h-3" /></a>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
